import { CircuitType, VoltageLevelType } from '@/api/circuit';
import { Form, Select } from '@maxtropy/components';
import { Col, FormInstance } from 'antd';
import React, { FC, useEffect, useMemo } from 'react';

const cabinetTypeList = [
  {
    label: '无',
    value: 0,
  },
  {
    label: '中置柜',
    value: 1,
  },
  {
    label: '环网柜',
    value: 2,
  },
];

const switchTypeList = [
  {
    label: '无',
    value: 0,
  },
  {
    label: '断路器',
    value: 1,
  },
  {
    label: '断路器-带隔离',
    value: 2,
  },
  {
    label: '断路器-不带隔离',
    value: 3,
  },
  {
    label: '开关',
    value: 4,
  },
];

const switchStatusList = [
  {
    label: '无',
    value: 0,
  },
  {
    label: '合闸',
    value: 1,
  },
  {
    label: '开闸',
    value: 2,
  },
];

export interface ICabinetSwitch {
  form: FormInstance;
  type: number;
  voltageLevel: number;
  voltageLevelIn: number;
  isEdit?: boolean;
}

const CabinetSwitch: FC<ICabinetSwitch> = props => {
  const { form, type, voltageLevel, voltageLevelIn, isEdit } = props;
  const cabinetType: number = Form.useWatch('cabinetType', form);

  // 新建默认值
  useEffect(() => {
    if (!isEdit) {
      // 高压的
      if (voltageLevel !== VoltageLevelType.ZERO_POINT_FOUR && voltageLevelIn !== VoltageLevelType.ZERO_POINT_FOUR) {
        switch (type) {
          case CircuitType.TRANSFORMER_CIRCUIT:
            form.setFieldsValue({
              cabinetType: 1,
              switchType: 0,
              switchStatus: 0,
            });
            break;
          case CircuitType.GRID_BILLING_CIRCUIT:
          case CircuitType.INCOMING_CIRCUIT:
          case CircuitType.OUTGOING_CIRCUIT:
          case CircuitType.LEAD_WIRE_SWITCH_CIRCUIT:
            form.setFieldsValue({
              cabinetType: 1,
              switchType: 1,
              switchStatus: 1,
            });
            break;
          case CircuitType.BUS_COUPLER_CIRCUIT:
            form.setFieldsValue({
              cabinetType: 1,
              switchType: 2,
              switchStatus: 1,
            });
            break;
          case CircuitType.VOLTAGE_CONVERSION_CIRCUIT:
            form.setFieldsValue({
              cabinetType: 1,
              switchType: 0,
              switchStatus: 0,
            });
            break;

          default:
            break;
        }
      } else {
        switch (type) {
          case CircuitType.TRANSFORMER_CIRCUIT:
            form.setFieldsValue({
              switchType: 0,
              switchStatus: 0,
            });
            break;
          case CircuitType.GRID_BILLING_CIRCUIT:
          case CircuitType.INCOMING_CIRCUIT:
          case CircuitType.BUS_COUPLER_CIRCUIT:
          case CircuitType.OUTGOING_CIRCUIT:
            form.setFieldsValue({
              switchType: 1,
              switchStatus: 1,
            });
            break;

          default:
            break;
        }
      }
    }
  }, [isEdit, voltageLevel, voltageLevelIn, type]);

  // 配电柜类型和开关类型(高压)
  const renderHighCircuitCabinetSwitchType = useMemo(() => {
    switch (type) {
      // 高压变压器回路只有中置柜
      case CircuitType.TRANSFORMER_CIRCUIT:
        return (
          <>
            <Col span={8}>
              <Form.Item
                name="cabinetType"
                label="所属柜类型"
                rules={[{ required: true, message: '请选择所属柜类型' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={cabinetTypeList.filter(i => i.value === 1)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchType"
                label="开关类型"
                rules={[{ required: true, message: '请选择开关类型' }]}
                initialValue={isEdit ? undefined : 0}
              >
                <Select
                  placeholder="请选择"
                  options={switchTypeList.filter(i => i.value === 0)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchStatus"
                label="开关状态"
                rules={[{ required: true, message: '请选择开关状态' }]}
                initialValue={isEdit ? undefined : 0}
              >
                <Select
                  placeholder="请选择"
                  options={switchStatusList.filter(i => i.value === 0)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </>
        );
      // 电网计费回路
      case CircuitType.GRID_BILLING_CIRCUIT:
        return (
          <>
            <Col span={8}>
              <Form.Item
                name="cabinetType"
                label="所属柜类型"
                rules={[{ required: true, message: '请选择所属柜类型' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={cabinetTypeList.filter(i => i.value === 1)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchType"
                label="开关类型"
                rules={[{ required: true, message: '请选择开关类型' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={switchTypeList.filter(i => i.value === 1)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchStatus"
                label="开关状态"
                rules={[{ required: true, message: '请选择开关状态' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={switchStatusList.filter(i => i.value !== 0)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </>
        );
      // 进线回路环网柜和中置柜
      case CircuitType.INCOMING_CIRCUIT:
      case CircuitType.OUTGOING_CIRCUIT:
        return (
          <>
            <Col span={8}>
              <Form.Item
                name="cabinetType"
                label="所属柜类型"
                rules={[{ required: true, message: '请选择所属柜类型' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  onChange={v => {
                    form.setFieldsValue({
                      switchType: v === 1 ? 1 : 4,
                      switchStatus: 1,
                    });
                  }}
                  placeholder="请选择"
                  options={cabinetTypeList.filter(i => i.value !== 0)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchType"
                label="开关类型"
                rules={[{ required: true, message: '请选择开关类型' }]}
                initialValue={isEdit ? undefined : cabinetType === 1 ? 1 : 4}
              >
                <Select
                  placeholder="请选择"
                  options={
                    cabinetType === 1
                      ? switchTypeList.filter(i => i.value === 1)
                      : switchTypeList.filter(i => i.value === 4)
                  }
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchStatus"
                label="开关状态"
                rules={[{ required: true, message: '请选择开关状态' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={switchStatusList.filter(i => i.value !== 0)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </>
        );

      // 母联回路
      case CircuitType.BUS_COUPLER_CIRCUIT:
        return (
          <>
            <Col span={8}>
              <Form.Item
                name="cabinetType"
                label="所属柜类型"
                rules={[{ required: true, message: '请选择所属柜类型' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={cabinetTypeList.filter(i => i.value === 1)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchType"
                label="开关类型"
                rules={[{ required: true, message: '请选择开关类型' }]}
                initialValue={isEdit ? undefined : 2}
              >
                <Select
                  placeholder="请选择"
                  options={switchTypeList.filter(i => i.value === 2 || i.value === 3)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchStatus"
                label="开关状态"
                rules={[{ required: true, message: '请选择开关状态' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={switchStatusList.filter(i => i.value !== 0)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </>
        );
      // 压变回路
      case CircuitType.VOLTAGE_CONVERSION_CIRCUIT:
        return (
          <>
            <Col span={8}>
              <Form.Item
                name="cabinetType"
                label="所属柜类型"
                rules={[{ required: true, message: '请选择所属柜类型' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={cabinetTypeList.filter(i => i.value !== 0)}
                  showSearch
                  optionFilterProp="label"
                  onChange={v => {
                    if (v === 1) {
                      form.setFieldsValue({
                        switchType: 0,
                        switchStatus: 0,
                      });
                    } else {
                      form.setFieldsValue({
                        switchType: 4,
                        switchStatus: 1,
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchType"
                label="开关类型"
                rules={[{ required: true, message: '请选择开关类型' }]}
                initialValue={isEdit ? undefined : cabinetType === 1 ? 0 : 4}
              >
                <Select
                  placeholder="请选择"
                  options={
                    cabinetType === 1
                      ? switchTypeList.filter(i => i.value === 0)
                      : switchTypeList.filter(i => i.value === 4)
                  }
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchStatus"
                label="开关状态"
                rules={[{ required: true, message: '请选择开关状态' }]}
                initialValue={isEdit ? undefined : cabinetType === 1 ? 0 : 1}
              >
                <Select
                  placeholder="请选择"
                  options={
                    cabinetType === 1
                      ? switchStatusList.filter(i => i.value === 0)
                      : switchStatusList.filter(i => i.value !== 0)
                  }
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </>
        );
      // 引闸刀回路
      case CircuitType.LEAD_WIRE_SWITCH_CIRCUIT:
        return (
          <>
            <Col span={8}>
              <Form.Item
                name="cabinetType"
                label="所属柜类型"
                rules={[{ required: true, message: '请选择所属柜类型' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={cabinetTypeList.filter(i => i.value === 1)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchType"
                label="开关类型"
                rules={[{ required: true, message: '请选择开关类型' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={switchTypeList.filter(i => i.value === 1)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchStatus"
                label="开关状态"
                rules={[{ required: true, message: '请选择开关状态' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={switchStatusList.filter(i => i.value !== 0)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </>
        );
      default:
        return <></>;
    }
  }, [type, cabinetType]);

  const renderLowCircuitSwitchType = useMemo(() => {
    switch (type) {
      // 变压器回路
      case CircuitType.TRANSFORMER_CIRCUIT:
        return (
          <>
            <Col span={8}>
              <Form.Item
                name="switchType"
                label="开关类型"
                rules={[{ required: true, message: '请选择开关类型' }]}
                initialValue={isEdit ? undefined : 0}
              >
                <Select
                  placeholder="请选择"
                  options={switchTypeList.filter(i => i.value === 0)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchStatus"
                label="开关状态"
                rules={[{ required: true, message: '请选择开关状态' }]}
                initialValue={isEdit ? undefined : 0}
              >
                <Select
                  placeholder="请选择"
                  options={switchStatusList.filter(i => i.value === 0)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </>
        );
      // 电网计费回路、进线回路、母联回路
      case CircuitType.GRID_BILLING_CIRCUIT:
      case CircuitType.INCOMING_CIRCUIT:
      case CircuitType.BUS_COUPLER_CIRCUIT:
        return (
          <>
            <Col span={8}>
              <Form.Item
                name="switchType"
                label="开关类型"
                rules={[{ required: true, message: '请选择开关类型' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={switchTypeList.filter(i => i.value === 1)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchStatus"
                label="开关状态"
                rules={[{ required: true, message: '请选择开关状态' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={switchStatusList.filter(i => i.value !== 0)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </>
        );
      // 出线回路
      case CircuitType.OUTGOING_CIRCUIT:
        return (
          <>
            <Col span={8}>
              <Form.Item
                name="switchType"
                label="开关类型"
                rules={[{ required: true, message: '请选择开关类型' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={switchTypeList.filter(i => i.value === 1 || i.value === 4)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="switchStatus"
                label="开关状态"
                rules={[{ required: true, message: '请选择开关状态' }]}
                initialValue={isEdit ? undefined : 1}
              >
                <Select
                  placeholder="请选择"
                  options={switchStatusList.filter(i => i.value !== 0)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </>
        );

      default:
        return <></>;
    }
  }, [type, cabinetType]);

  return (
    <>
      {voltageLevel || voltageLevelIn ? (
        voltageLevel !== VoltageLevelType.ZERO_POINT_FOUR && voltageLevelIn !== VoltageLevelType.ZERO_POINT_FOUR ? (
          renderHighCircuitCabinetSwitchType
        ) : (
          renderLowCircuitSwitchType
        )
      ) : (
        <></>
      )}
    </>
  );
};

export default CabinetSwitch;
